// components/ui/Toast.tsx
// -----------------------------------------------------------------------------
// Toast notification system for displaying feedback messages.
// -----------------------------------------------------------------------------

import {
  createContext,
  useContext,
  useState,
  useCallback,
  type ReactNode,
} from "react";
import { Icon } from "./Icon";
import { cn } from "./cn";

// Toast types
type ToastType = "success" | "error" | "info" | "warning";

interface Toast {
  id: string;
  message: string;
  type: ToastType;
  duration?: number;
}

interface ToastContextType {
  toasts: Toast[];
  showToast: (message: string, type?: ToastType, duration?: number) => void;
  dismissToast: (id: string) => void;
}

const ToastContext = createContext<ToastContextType | null>(null);

/**
 * Hook to use the toast notification system.
 */
export function useToast() {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error("useToast must be used within a ToastProvider");
  }
  return context;
}

interface ToastProviderProps {
  children: ReactNode;
}

/**
 * Provider component for the toast notification system.
 * Wrap your app with this to enable toast notifications.
 */
export function ToastProvider({ children }: ToastProviderProps) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const showToast = useCallback(
    (message: string, type: ToastType = "info", duration: number = 4000) => {
      const id = `toast-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;

      setToasts((prev) => [...prev, { id, message, type, duration }]);

      // Auto-dismiss after duration
      if (duration > 0) {
        setTimeout(() => {
          setToasts((prev) => prev.filter((t) => t.id !== id));
        }, duration);
      }
    },
    []
  );

  const dismissToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  return (
    <ToastContext.Provider value={{ toasts, showToast, dismissToast }}>
      {children}
      <ToastContainer toasts={toasts} onDismiss={dismissToast} />
    </ToastContext.Provider>
  );
}

interface ToastContainerProps {
  toasts: Toast[];
  onDismiss: (id: string) => void;
}

/**
 * Container component that renders all active toasts.
 */
function ToastContainer({ toasts, onDismiss }: ToastContainerProps) {
  if (toasts.length === 0) return null;

  return (
    <div className="fixed bottom-4 right-4 z-50 flex flex-col gap-2 max-w-sm">
      {toasts.map((toast) => (
        <ToastItem key={toast.id} toast={toast} onDismiss={onDismiss} />
      ))}
    </div>
  );
}

interface ToastItemProps {
  toast: Toast;
  onDismiss: (id: string) => void;
}

/**
 * Individual toast notification item.
 */
function ToastItem({ toast, onDismiss }: ToastItemProps) {
  const iconName = getIconName(toast.type);
  const iconColor = getIconColor(toast.type);

  return (
    <div
      className={cn(
        "flex items-start gap-3 p-4 rounded-lg shadow-lg border",
        "bg-surface-1 border-border-tertiary",
        "animate-in slide-in-from-right-5 fade-in duration-300"
      )}
      role="alert"
    >
      <Icon name={iconName} size={20} className={iconColor} />
      <p className="flex-1 text-body-2 text-text-primary">{toast.message}</p>
      <button
        onClick={() => onDismiss(toast.id)}
        className="text-text-tertiary hover:text-text-primary transition-colors"
        aria-label="Dismiss"
      >
        <Icon name="xmark" size={16} />
      </button>
    </div>
  );
}

function getIconName(type: ToastType): string {
  switch (type) {
    case "success":
      return "circle-check";
    case "error":
      return "circle-xmark";
    case "warning":
      return "circle-question";
    case "info":
    default:
      return "circle";
  }
}

function getIconColor(type: ToastType): string {
  switch (type) {
    case "success":
      return "text-olive";
    case "error":
      return "text-red";
    case "warning":
      return "text-yellow";
    case "info":
    default:
      return "text-text-secondary";
  }
}

export type { Toast, ToastType };
